import { JSX, SVGProps, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Table, TableColumnsType, Tag } from "antd";
import { CardContainer } from "../../components/Card/CardContainer";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import Text from "../../components/Text/Text";
import Button from "../../components/Button/Button";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import { GreyTableContainer } from "../../components/Table/GreyTable";
import { useLocalStorage } from "../../hook/useLocalStorage";
import color from "../../resource/color";
import { resolveShopTierConfig } from "../ShopTier/config";
import { generateShopTierMockData, AuditLogEntry } from "../ShopTier/mockData";
import { actionColorOf } from "../ShopTier/actionColor";
import {
  MonitorCard,
  MonitorFacts,
  ProgressTrack,
  ProgressBar,
  ProgressScale,
  NavCard,
  BenefitSection,
  BenefitList,
} from "../ShopTier/styled";

function parseAmount(label: string): number {
  return parseFloat(label.replace(/,/g, "")) || 0;
}

function tierThreshold(subLabel: string): number {
  const match = subLabel.match(/[\d,]+(\.\d+)?/);
  return match ? parseFloat(match[0].replace(/,/g, "")) : 0;
}

function formatAmount(value: number, volumeUnit: string): string {
  return volumeUnit === "ล้านบาท" ? value.toFixed(1) : Math.round(value).toLocaleString("th-TH");
}

function parseThaiDate(d: string): number {
  const [day, month, year] = d.split("/").map(Number);
  return new Date(year || 0, (month || 1) - 1, day || 1).getTime();
}

function CheckIcon({ size = 14, ...props }: SVGProps<SVGSVGElement> & { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

const historyColumns: TableColumnsType<AuditLogEntry> = [
  {
    title: "วันที่",
    key: "date",
    render: (_, r) => (
      <div>
        <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">{r.date}</Text>
        <div>
          <Text fontFamily="Sarabun" fontSize={12} color="Text3">{r.time}</Text>
        </div>
      </div>
    ),
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "action",
    render: (a: string) => (
      <Tag style={{ borderRadius: 16, border: "none" }} color={actionColorOf(a)}>
        <Text fontFamily="Sarabun" fontSize={13} fontWeight={700} color="white">{a}</Text>
      </Tag>
    ),
  },
  {
    title: "ก่อน",
    dataIndex: "before",
    key: "before",
    render: (v: string) => <Text fontFamily="Sarabun" fontSize={14} color="Text2">{v}</Text>,
  },
  {
    title: "หลัง",
    dataIndex: "after",
    key: "after",
    render: (v: string) => <Text fontFamily="Sarabun" fontSize={14} color="Text2">{v}</Text>,
  },
  {
    title: "เหตุผล",
    dataIndex: "reason",
    key: "reason",
    render: (v: string) => <Text fontFamily="Sarabun" fontSize={14} color="Text2">{v}</Text>,
  },
  {
    title: "ผู้ดำเนินการ",
    dataIndex: "actor",
    key: "actor",
    render: (v: string) => <Text fontFamily="Sarabun" fontSize={14} color="Text2">{v}</Text>,
  },
];

function ShopTierDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [company] = useLocalStorage("profile", null);
  const config = resolveShopTierConfig(company?.company);
  const mockData = generateShopTierMockData(config);
  const shop = mockData.shops[0]
  const [isUploading, setIsUploading] = useState(shop?.navSynced ?? false);
  const [showConfirmUpload, setShowConfirmUpload] = useState(false);
  const [showConfirmUndo, setShowConfirmUndo] = useState(false);

  const currentYearConfig = mockData.configYears.find((y) => y.year === "ปี 2569");
  const tierBenefit = currentYearConfig?.tiers.find((t) => t.tierKey === shop.tier);

  const tierIndex = config.tiers.findIndex((t) => t.key === shop.tier);
  const currentTier = config.tiers[tierIndex];
  const nextTier = config.tiers[tierIndex + 1];
  const currentMin = currentTier ? tierThreshold(currentTier.subLabel) : 0;
  const nextMin = nextTier ? tierThreshold(nextTier.subLabel) : currentMin;
  const volumeValue = parseAmount(shop.volumeLabel);
  const percentToNext = nextTier
    ? Math.min(100, Math.max(0, ((volumeValue - currentMin) / (nextMin - currentMin)) * 100))
    : 100;
  const remaining = nextTier ? Math.max(0, nextMin - volumeValue) : 0;

  const shopHistory = mockData.auditLog
    .filter((a) => a.code === shop.code)
    .sort((a, b) => parseThaiDate(b.date) - parseThaiDate(a.date));

  return (
    <CardContainer>
      <PageTitleNested
        title={shop.shop}
        showBack
        onBack={() => navigate(-1)}
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "ระดับร้านค้า", path: -1 },
              { text: "รายละเอียดร้านค้า", path: window.location.pathname },
            ]}
          />
        }
        description={
          <Text fontFamily="Sarabun" fontSize={13} color="Text3">
            รหัสร้านค้า {shop.code} · เลขนิติบุคคล {shop.taxId} · จ.{shop.province} · {shop.zone}
          </Text>
        }
      />

      <MonitorCard style={{ marginTop: 20 }}>
        <MonitorFacts>
          <div>
            <Text fontSize={13} color="Text3">ระดับปัจจุบัน</Text>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
              <Tag style={{ borderRadius: 16, border: "none" }} color={currentTier?.color || color.Grey}>
                <Text fontFamily="Sarabun" fontSize={14} fontWeight={700} color="white">{shop.tier}</Text>
              </Tag>
              <Tag style={{ borderRadius: 16, border: "none" }} color={shop.changedBy === "ระบบ" ? color.success : color.softWarning}>
                <Text fontFamily="Sarabun" fontSize={13} fontWeight={700} color="white">{shop.changedBy}</Text>
              </Tag>
            </div>
          </div>
          <div>
            <Text fontSize={13} color="Text3">ยอดสะสมปี 2569</Text>
            <div style={{ marginTop: 6 }}>
              <Text fontWeight={700} fontSize={20}>{shop.volumeLabel} {config.volumeUnit}</Text>
            </div>
            {tierBenefit && (
              <Text fontSize={12} color="Text3">
                {config.percentLabel} ระดับนี้ {tierBenefit.percent}%
              </Text>
            )}
          </div>
          <div>
            <Text fontSize={13} color="Text3">ระดับถัดไป</Text>
            <div style={{ marginTop: 6 }}>
              <Text fontWeight={700} fontSize={16}>
                {nextTier ? `${nextTier.label} ที่ ${formatAmount(nextMin, config.volumeUnit)} ${config.volumeUnit}` : "ระดับสูงสุดแล้ว"}
              </Text>
            </div>
            <Text fontSize={12} color="Text3">เปลี่ยนระดับล่าสุด {shop.changedDate}</Text>
          </div>
        </MonitorFacts>
        <ProgressTrack>
          <ProgressBar $percent={percentToNext} />
        </ProgressTrack>
        <ProgressScale>
          <span>{formatAmount(currentMin, config.volumeUnit)} {config.volumeUnit}</span>
          <span>{nextTier ? `${formatAmount(nextMin, config.volumeUnit)} ${config.volumeUnit}` : "-"}</span>
        </ProgressScale>
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
          {nextTier ? (
            <>
              <Text fontSize={13} color="Text2">
                เหลืออีก <Text fontWeight={700} fontSize={13}>{formatAmount(remaining, config.volumeUnit)} {config.volumeUnit}</Text> จึงถึงระดับ {nextTier.label}
              </Text>
              <Tag style={{ borderRadius: 16, border: "none" }} color={color.secondary2}>
                <Text fontFamily="Sarabun" fontSize={12} fontWeight={700} color="white">{Math.round(percentToNext)}%</Text>
              </Tag>
            </>
          ) : (
            <Text fontSize={13} color="Text3">ร้านค้าอยู่ในระดับสูงสุดแล้ว</Text>
          )}
        </div>
      </MonitorCard>

      <NavCard style={{ alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Text fontWeight={700}>การอัปเดต Navision</Text>
            <Tag style={{ borderRadius: 16, border: "none" }} color={isUploading ? color.greenBackground : color.orangeBackground}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, color: isUploading ? color.darkGreen : color.Disable }}>
                {isUploading && <CheckIcon />}
                <Text fontFamily="Sarabun" fontSize={12} fontWeight={700} color={isUploading ? "darkGreen" : "Disable"}>
                  {isUploading ? "อัปโหลดแล้ว" : "รออัปโหลด"}
                </Text>
              </div>
            </Tag>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginTop: 12 }}>
            <div>
              <Text fontSize={12} color="Text3">รหัสร้านค้า</Text>
              <div><Text fontWeight={700} fontSize={14}>{shop.code}</Text></div>
            </div>
            <div>
              <Text fontSize={12} color="Text3">เลขนิติบุคคล</Text>
              <div><Text fontWeight={700} fontSize={14}>{shop.taxId}</Text></div>
            </div>
            <div>
              <Text fontSize={12} color="Text3">ระดับที่ต้องคีย์</Text>
              <div>
                <Text fontWeight={700} fontSize={14}>
                  {shop.history[1]?.tier || "-"} → {shop.tier}
                </Text>
              </div>
            </div>
            <div>
              <Text fontSize={12} color="Text3">วันที่มีผล</Text>
              <div><Text fontWeight={700} fontSize={14}>{shop.changedDate}</Text></div>
            </div>
            <div>
              <Text fontSize={12} color="Text3">% {config.percentLabel}</Text>
              <div><Text fontWeight={700} fontSize={14}>{tierBenefit ? `${tierBenefit.percent}%` : "-"}</Text></div>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <Text fontSize={12} color="Text3">
              {isUploading
                ? shop.navBy && shop.navAt
                  ? `อัปเดตแล้วโดย ${shop.navBy} เมื่อ ${shop.navAt}`
                  : "ทำเครื่องหมายว่าอัปโหลดแล้ว"
                : "ยังไม่ได้คีย์เข้า Navision ให้คีย์ข้อมูลชุดนี้ในระบบ Navision ก่อน แล้วจึงกลับมาทำเครื่องหมาย"}
            </Text>
          </div>
        </div>
        <Button
          typeButton={isUploading ? "primary-light" : "primary"}
          title={isUploading ? "ยกเลิกการทำเครื่องหมาย":"ทำเครื่องหมายว่าอัปโหลดแล้ว"}
          style={{ width: "auto" }}
          onClick={() => (isUploading ? setShowConfirmUndo(true) : setShowConfirmUpload(true))}
        />
      </NavCard>

      <ConfirmModal
        visible={showConfirmUpload}
        title="ยืนยันการอัปเดต Navision"
        desc={`ยืนยันว่าคีย์ระดับ ${shop.tier} ของ ${shop.shop} (${shop.code}) เข้า Navision เรียบร้อยแล้ว บันทึกลง Audit Log ทุกครั้ง`}
        onConfirm={() => {
          setIsUploading(true);
          setShowConfirmUpload(false);
        }}
        onCancel={() => setShowConfirmUpload(false)}
      />

      <ConfirmModal
        visible={showConfirmUndo}
        title="ยืนยันการอัปเดต Navision"
        desc={`ยืนยันการยกเลิกเครื่องหมายของ ${shop.shop} (${shop.code}) ระบบจะกลับไปเป็น รออัปโหลด และบันทึกลง Audit Log`}
        onConfirm={() => {
          setIsUploading(false);
          setShowConfirmUndo(false);
        }}
        onCancel={() => setShowConfirmUndo(false)}
      />

      <BenefitSection>
        <NavCard style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <Text fontWeight={700} fontSize={16}>สิทธิประโยชน์ของระดับปัจจุบัน</Text>
        <Text fontSize={12} color="Text3">ข้อมูลแสดงผล ตรงกับที่ร้านค้าเห็นในแอป</Text>
        <BenefitList style={{ marginTop: 0 }}>
          {(tierBenefit?.benefits || []).map((b) => (
            <li key={b}>
              <Text fontFamily="Sarabun" fontSize={14} color="Text2">{b}</Text>
            </li>
          ))}
        </BenefitList>
        </NavCard>
      </BenefitSection>

      <Text fontWeight={700} fontSize={14}>ประวัติทั้งหมด</Text>
      <div style={{ height: 16 }} />
      <GreyTableContainer>
        <Table
          rowKey={(r: AuditLogEntry) => `${r.date}-${r.time}-${r.action}`}
          dataSource={shopHistory}
          columns={historyColumns}
          pagination={{ pageSize: 10 }}
        />
      </GreyTableContainer>
    </CardContainer>
  );
}

export default ShopTierDetailPage;

